import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { Form, useFormikContext } from 'formik';
import { SaveEmailDialog } from '@/components/dialogs/SaveEmailDialog';
import { ComposeDetailsSection } from '@/components/Drawers/AddUnlayerEmailDrawer/ComposeDetailsSection';
import { EditorSection } from '@/components/Drawers/AddUnlayerEmailDrawer/EditorSection';
import { ComposeFooterActions } from '@/components/Drawers/AddUnlayerEmailDrawer/ComposeFooterActions';
import { DeleteDraftDialog } from '@/components/Drawers/AddUnlayerEmailDrawer/dialogs/DeleteDraftDialog';
import { LoadDesignJsonDialog } from '@/components/Drawers/AddUnlayerEmailDrawer/dialogs/LoadDesignJsonDialog';
import { ReplaceDraftDialog } from '@/components/Drawers/AddUnlayerEmailDrawer/dialogs/ReplaceDraftDialog';
import { SaveTemplateDialog } from '@/components/Drawers/AddUnlayerEmailDrawer/dialogs/SaveTemplateDialog';
import { usePresetTemplateLoader } from '@/components/Drawers/AddUnlayerEmailDrawer/hooks/usePresetTemplateLoader';
import { usePresetDraftLoader } from '@/components/Drawers/AddUnlayerEmailDrawer/hooks/usePresetDraftLoader';
import { AddFileDialog } from '@/components/dialogs/AddFileDialog';
import { useSelector } from 'react-redux';
import { useUnlayerUIContext } from '@/context/UnlayerUIContext';
import { useUnlayerComposeInteractionsContext } from '@/context/UnlayerComposeInteractionsContext';
import { FolioDrawer } from '@/components/Drawers/FolioDrawer';
import { FolioDrawerHeader } from '@/components/Drawers/FolioDrawer/FolioDrawerHeader';

/**
 * Renders the compose drawer UI and related modal flows for the Unlayer email composer.
 */
export const AddUnlayerEmailForm = () => {
	const open = useSelector((state) => state.dialog?.open?.unlayerEmail) ?? false;
	const dialogData = useSelector((state) => state.dialog?.data?.unlayerEmail) ?? {};
	const {
		closeLoadDesignDialog,
		closeSaveDialog,
		designLoadRevision,
		isLoadDesignJsonDialogOpen,
		loadDesignJsonError,
		loadDesignJsonValue,
		setDesignToLoad,
		showSaveDialog,
	} = useUnlayerUIContext();
	const { handleClose, handleLoadDesignJson, handleLoadDesignJsonValueChange } =
		useUnlayerComposeInteractionsContext();
	const { handleSubmit, dirty } = useFormikContext();

	usePresetTemplateLoader();
	usePresetDraftLoader();

	useEffect(() => {
		if (!open) return;
		if (!dialogData?.editorDesign) return;
		if (designLoadRevision > 0) return;
		setDesignToLoad(dialogData.editorDesign);
	}, [dialogData?.editorDesign, open, designLoadRevision, setDesignToLoad]);

	return (
		<Form>
			<FolioDrawer
				open={open}
				onClose={handleClose}
				canMaximise
				maximisedSize='full'
				confirmClose={dirty}
			>
				<FolioDrawerHeader title='Compose Email' />

				<Box
					sx={{
						px: 2,
						pt: 1,
						pb: 2,
						flex: 1,
						minHeight: 0,
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
						overflowY: 'auto',
					}}
				>
					<ComposeDetailsSection />
					<EditorSection />
				</Box>

				<ComposeFooterActions />
			</FolioDrawer>

			{showSaveDialog && (
				<SaveEmailDialog
					open={showSaveDialog}
					handleClose={closeSaveDialog}
					handleSave={() => {
						// Submit before closing so Formik still has the latest dialog values
						// (including the selected follow-up due date).
						handleSubmit();
						closeSaveDialog();
					}}
				/>
			)}
			<SaveTemplateDialog />
			<DeleteDraftDialog />
			<ReplaceDraftDialog />
			<LoadDesignJsonDialog
				open={isLoadDesignJsonDialogOpen}
				onClose={closeLoadDesignDialog}
				loadDesignJsonValue={loadDesignJsonValue}
				loadDesignJsonError={loadDesignJsonError}
				onValueChange={handleLoadDesignJsonValueChange}
				onLoad={handleLoadDesignJson}
			/>
			<AddFileDialog />
		</Form>
	);
};
